import type { CompanyFinancialsScenarioType } from '@/services/companies.service'
import { isSectionOrVariant, orderSections } from '@/lib/financials-sections'

export const FINANCIAL_SCENARIOS = [
  { value: 'ACTUAL', label: 'Actual' },
  { value: 'BUDGET', label: 'Budget' },
] satisfies ReadonlyArray<{
  value: CompanyFinancialsScenarioType
  label: string
}>

type RowWithSection = { globalSectionName?: string | null }

const BUDGET_STATEMENT_SECTIONS = ['P&L', 'Cash Flows'] as const

function isBudgetStatementSection(sectionName: string | null | undefined) {
  return (
    typeof sectionName === 'string' &&
    BUDGET_STATEMENT_SECTIONS.some((canonicalSection) =>
      isSectionOrVariant(sectionName, canonicalSection)
    )
  )
}

export function filterFinancialRowsForScenario<T extends RowWithSection>(
  rows: readonly T[],
  scenarioType: CompanyFinancialsScenarioType
): T[] {
  if (scenarioType === 'ACTUAL') return [...rows]
  return rows.filter((row) => isBudgetStatementSection(row.globalSectionName))
}

export function getFinancialSectionsForScenario<T extends RowWithSection>(
  rows: readonly T[],
  scenarioType: CompanyFinancialsScenarioType
): string[] {
  const availableSections = filterFinancialRowsForScenario(rows, scenarioType)
    .map(({ globalSectionName }) => globalSectionName)
    .filter(
      (sectionName): sectionName is string =>
        typeof sectionName === 'string' && sectionName.length > 0
    )

  const orderedAvailableSections = orderSections(availableSections)
  if (scenarioType === 'ACTUAL') return orderedAvailableSections

  const missingCanonicalSections = BUDGET_STATEMENT_SECTIONS.filter(
    (canonicalSection) =>
      !orderedAvailableSections.some((sectionName) =>
        isSectionOrVariant(sectionName, canonicalSection)
      )
  )
  return [...orderedAvailableSections, ...missingCanonicalSections]
}

export function isFinancialRowBlank(
  values: Readonly<Record<string, number | null | undefined>>,
  scenarioType: CompanyFinancialsScenarioType
): boolean {
  if (scenarioType === 'BUDGET') {
    return Object.values(values).every((value) => value == null)
  }

  return Object.values(values).every((value) => value == null || value === 0)
}

export function getFinancialsEmptyState(
  scenarioType: CompanyFinancialsScenarioType,
  sectionName?: string | null
): { title: string; description: string } {
  if (scenarioType === 'BUDGET') {
    const statementName = sectionName ?? 'P&L or Cash Flows'
    return {
      title: `No ${statementName} Budget data available`,
      description: `This company does not have ${statementName} Budget data for the selected period.`,
    }
  }

  return {
    title: 'No data available yet.',
    description: "Once entries are added, they'll appear here automatically.",
  }
}
